import React, { useEffect, useReducer, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { changeAutoshipPlans, getAutoshipPlans } from 'api/jalapeno';
import { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import { ADDITIONAL_DIETARY_PREFERENCES, DIETARY_PREFERENCES_V2, ORGANIC_OPTIONS } from 'constants/signup';
import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import { subscriptionPacks } from 'data/subscriptionData';
import PlanCard from 'features/account/my-plan/PlanCard';
import TaxesApply from 'features/account/my-plan/TaxesApply';
import Button from 'honeydew/Button';
import { CardInput } from 'honeydew/CardInput';
import Checkbox from 'honeydew/Checkbox';
import { Divider } from 'honeydew/Divider';
import { DietaryPreferencesIcon } from 'honeydew/icons/DietaryPreferences';
import useGlobalStore from 'hooks/useGlobalStore';
import { EditHouseholdMealsForm } from 'shared/EditHouseholdMealsForm';
import { trackEvent } from 'utils/analyticsUtils';
import logError from 'utils/errorUtils';
import { getCamelCaseFromKebabCase } from 'utils/globalUtils';
import { getDietaryPreferencesIconName } from 'utils/productUtils';

import planReducer from './reducer';

export function EditDietaryPreferencesModule({ account, setError, setIsEditing }) {
    const queryClient = useQueryClient();
    const { isMisfitsTheme } = useGlobalStore();
    const { dietaryPreferences, planPreferences, subID } = account ?? {};
    const { maxBoxRange, minBoxRange } = planPreferences ?? {};
    const { cookingFrequency, dietaryPrefSplit, householdSize } = dietaryPreferences ?? {};

    const [subscriptionPlan, setSubscriptionPlan] = useState({ ...dietaryPreferences, plans: planPreferences.plans });
    const [availablePlans, setAvailablePlans] = useState({ planSizes: [], plans: [] });
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [showOrderMinError, setShowOrderMinError] = useState(false);

    const [state, dispatch] = useReducer(planReducer, subscriptionPlan);

    const isDynamicDietaryPreferences = dietaryPrefSplit === 'dynamic';

    useEffect(() => {
        setSubscriptionPlan(state);

        if (availablePlans?.plans?.length > 0) {
            const invalidPreferences = state?.plans?.length === 0;
            setShowOrderMinError(invalidPreferences);
            setIsSubmitDisabled(invalidPreferences);
        }
    }, [state]);

    const handleSubmitPlanChange = async (e) => {
        e.preventDefault();
        setIsSubmitDisabled(true);

        setShowOrderMinError(false);

        try {
            const response = await changeAutoshipPlans(subID, subscriptionPlan);

            if (response.msg === 'Success') {
                queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                setIsEditing(false);
                setIsSubmitDisabled(false);
                window.scrollTo({ top: 0 });
                setError('');

                const newHouseholdSize = householdSize !== state.householdSize;
                const newCookingFrequency = cookingFrequency !== state.cookingFrequency;

                if (newHouseholdSize || newCookingFrequency) {
                    trackEvent('plan settings edit', {
                        location: 'my plan page',
                        household_size: newHouseholdSize ? state.householdSize : null,
                        cooking_frequency: newCookingFrequency ? state.cookingFrequency : null,
                    });
                }
            }
        } catch (err) {
            setIsSubmitDisabled(false);
            setError('Unable to update plan');
        }
    };

    const getAvailablePlanData = async () => {
        try {
            const response = await getAutoshipPlans();
            if (response.data) {
                setAvailablePlans(response.data);
            }
        } catch (e) {
            logError(e);
            console.error(e);
        }
    };

    useEffect(() => {
        getAvailablePlanData();
    }, [planPreferences]);

    const handleDietChange = (e) => {
        dispatch({ type: 'updateDiet', payload: e });
    };

    const handleAdditionalPreferenceChange = ({ e, label }) => {
        if (label === 'Kosher') dispatch({ type: 'updateKosher', payload: e });
        if (label === 'Gluten-Free') dispatch({ type: 'updateGlutenFree', payload: e });
        if (label === 'Non-GMO') dispatch({ type: 'updateNonGMO', payload: e });
        if (label === 'Organic') dispatch({ type: 'updateOrganic', payload: e ? 100 : 0 });
    };

    const handleOrganicPreferencesChange = (e) => {
        dispatch({ type: 'updateOrganic', payload: e });
    };

    const handleCookingFrequencyChange = (e) => {
        dispatch({ type: 'updateCookingFrequency', payload: e });
    };

    const handleHouseholdSizeChange = (e) => {
        dispatch({ type: 'updateHouseholdSize', payload: e });
    };

    return (
        <div>
            <div>
                <h4 className="text-body-md mb-5 font-grotesk-bold">Plan Settings</h4>

                <p className="text-body-sm">
                    Select from the preferences below. This will determine how we get your cart started each week.
                </p>

                <p className="text-body-sm my-10 inline-block rounded-sm bg-malt px-10 py-5">
                    <span className="font-grotesk-bold">Plan Range</span> ${minBoxRange}-${maxBoxRange} per week
                </p>

                <TaxesApply />

                <div className="mt-20">
                    <EditHouseholdMealsForm
                        cookingFrequency={state.cookingFrequency}
                        householdSize={state.householdSize}
                        handleCookingFrequencyChange={handleCookingFrequencyChange}
                        handleHouseholdSizeChange={handleHouseholdSizeChange}
                        editable={isDynamicDietaryPreferences}
                    />
                </div>
            </div>

            <Divider />

            <div>
                <h4 className="text-body-md mb-5 font-grotesk-bold">Cart Preferences</h4>
                <p className="text-body-sm mb-10 mt-20 font-grotesk-bold">How do you typically eat?</p>
                <div className="mx-auto grid gap-10">
                    {DIETARY_PREFERENCES_V2.map(({ icon, label, ...inputAttributes }) => (
                        <CardInput
                            inputAttributes={{
                                ...inputAttributes,
                                name: 'diet',
                                handleChange: handleDietChange,
                            }}
                            checked={state.dietaryPreference === inputAttributes.value}
                            key={inputAttributes.value}
                        >
                            <div className="flex items-center justify-between gap-5 px-10 text-center">
                                <p className="text-body-sm font-grotesk-bold">{label}</p>
                                <DietaryPreferencesIcon
                                    width={34}
                                    dietaryPreferencesIconName={icon}
                                    className="h-[34px]"
                                    brand={isMisfitsTheme ? MISFITS_THEME_DOMAIN : IMPERFECT_THEME_DOMAIN}
                                />
                            </div>
                        </CardInput>
                    ))}
                </div>

                <p className="text-body-sm mb-10 mt-20 font-grotesk-bold">
                    Do you have any other dietary needs or preferences?
                </p>
                <div className="mx-auto mb-30 grid gap-10">
                    {ADDITIONAL_DIETARY_PREFERENCES.map(({ label, value }) => (
                        <Checkbox
                            id={`${value}-additional-preference`}
                            data-testid={`${value}-additional-preference`}
                            name="additional-preference"
                            onCheckedChange={(e) => handleAdditionalPreferenceChange({ e, label })}
                            checked={
                                state[getCamelCaseFromKebabCase(value)] === true ||
                                (value === 'organic' && state.percentOrganic > 0)
                            }
                            key={value}
                            label={
                                <div className="flex items-center gap-5">
                                    <DietaryPreferencesIcon
                                        width={34}
                                        dietaryPreferencesIconName={getDietaryPreferencesIconName(label)}
                                        className="h-[34px]"
                                    />
                                    <div>
                                        <p className="text-body-sm font-grotesk-bold">{label}</p>

                                        {label === 'Organic' && (
                                            <>
                                                <p className="text-body-xs">
                                                    How much organic produce do you usually buy?
                                                </p>

                                                <div className="flex">
                                                    {ORGANIC_OPTIONS.map(
                                                        ({ shortLabel, ...organicInputAttributes }) => (
                                                            <CardInput
                                                                inputAttributes={{
                                                                    ...organicInputAttributes,
                                                                    name: 'organic-preferences',
                                                                    handleChange: handleOrganicPreferencesChange,
                                                                }}
                                                                checked={
                                                                    state.percentOrganic ===
                                                                    organicInputAttributes.value
                                                                }
                                                                key={organicInputAttributes.value}
                                                                extendedStyles={{
                                                                    wrapper: clsx(
                                                                        'rounded-none focus-within:outline-none shadow-transparent -ml-[1px] w-60 sm:w-[80px] text-center',
                                                                        {
                                                                            'rounded-l-md':
                                                                                organicInputAttributes.value ===
                                                                                ORGANIC_OPTIONS[0].value,
                                                                            'rounded-r-md':
                                                                                organicInputAttributes.value ===
                                                                                ORGANIC_OPTIONS[
                                                                                    ORGANIC_OPTIONS.length - 1
                                                                                ].value,
                                                                            'border !border-kale z-10':
                                                                                state.percentOrganic ===
                                                                                organicInputAttributes.value,
                                                                        }
                                                                    ),
                                                                }}
                                                            >
                                                                <p className="text-body-sm">{shortLabel}</p>
                                                            </CardInput>
                                                        )
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            }
                        />
                    ))}
                </div>
            </div>

            <p className="text-body-sm mb-15 font-grotesk-bold">What do you usually shop for each week?</p>
            <fieldset>
                {subscriptionPacks.map((aisle) => {
                    const savedPlan = subscriptionPlan?.plans.find((plan) => plan.id === aisle.id);

                    return (
                        <React.Fragment key={aisle.name}>
                            <PlanCard
                                dispatch={dispatch}
                                plan={aisle}
                                savedPlan={savedPlan}
                                savedPlanSize={subscriptionPlan?.planSize}
                                flexible
                            />
                        </React.Fragment>
                    );
                })}
            </fieldset>

            <Divider />

            <div>
                <p className="text-body-xs mb-15">Any updates will go into effect on your next shopping window.</p>

                {showOrderMinError && (
                    <p className="mb-15 font-grotesk-bold text-error">
                        Pack selections must meet or surpass order minimum. Make additional selection(s).
                    </p>
                )}
                <div className="max-w-[350px]">
                    <Button size="md" onClick={handleSubmitPlanChange} disabled={isSubmitDisabled}>
                        Update
                    </Button>
                    <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="text-body-sm link mx-auto mt-15 block"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

EditDietaryPreferencesModule.propTypes = {
    account: PropTypes.shape({}),
};

EditDietaryPreferencesModule.defaultProps = {
    account: {},
};
